import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  ListFilter,
  Share,
  ChevronDown,
  ArrowRight,
  FileText,
  Loader2,
  Compass,
} from "lucide-react";

import { LawyerLayout } from "@/pages/layouts/LawyerLayout";
import { useLawyerPersons } from "@/shared/hooks/useLawyerEstates";
import { LAWYER_ROUTES, DEMO_ROUTES } from "@/shared/constants/paths";
import { useDemoMode, useNavigateToDemoEstate } from "@/pages/demo/DemoModeContext";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
// import { useLawyerWelcomeSeen } from "@/shared/hooks/useLawyerWelcomeSeen";
import { formatCompactDollars, formatPersonDateForDisplay } from "@/shared/utils/formatters";
import { parseAddress, toStateAbbreviation } from "@/shared/utils/addressUtils";
import { US_STATES } from "@/shared/constants/constants";
import { cn } from "@/shared/utils/core";
import { CALENDLY_URLS } from "@/shared/constants/calendlyUrls";
import { CalendlyModal } from "@/shared/components/overlay/CalendlyModal";
import CircularProgress from "@/shared/components/CircularProgress";

import type { LawyerEstateRow } from "@/shared/hooks/useLawyerEstates";
import type { DashboardFilters } from "@/shared/types/lawyerDashboard";
import { EMPTY_FILTERS } from "@/shared/types/lawyerDashboard";

import { generateDashboardPdf } from "./utils/generateDashboardPdf";
import { InviteClientWizard } from "./components/InviteClientWizard/InviteClientWizard";
import { useLawyerClientInvitations } from "@/shared/hooks/useLawyerClientInvitations";
// import { AttorneyWelcomeModal } from "./components/AttorneyWelcomeModal";
import { DashboardEmptyState } from "./components/DashboardEmptyState";
import { FilterPanel } from "./components/FilterPanel";

interface LawyerCustomizations {
  lawyer_viewed_clients?: number[];
  [key: string]: unknown;
}

type SortKey =
  | "action"
  | "client-name"
  | "last-updated-newest"
  | "last-updated-oldest"
  | "dod-oldest";

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "action", label: "Action needed" },
  { key: "client-name", label: "Client Name (A to Z)" },
  { key: "last-updated-newest", label: "Last updated, newest first" },
  { key: "last-updated-oldest", label: "Last updated, oldest first" },
  { key: "dod-oldest", label: "Date of death, oldest" },
];

function parseLocation(location: string | null): { label: string; detail: string } | null {
  if (!location) return null;
  const parsed = parseAddress(location);
  const stateAbbr = toStateAbbreviation(parsed.state);
  const detail = parsed.city && stateAbbr ? `${parsed.city}, ${stateAbbr}` : stateAbbr || location;
  return { label: "Single state", detail };
}

function sortRows(rows: LawyerEstateRow[], sortKey: SortKey): LawyerEstateRow[] {
  const sorted = [...rows];
  switch (sortKey) {
    case "action":
      return sorted.sort((a, b) => {
        const aIsAction = a.highlight?.category === "action" ? 0 : 1;
        const bIsAction = b.highlight?.category === "action" ? 0 : 1;
        if (aIsAction !== bIsAction) return aIsAction - bIsAction;
        const aTime = a.assignedAt ?? "";
        const bTime = b.assignedAt ?? "";
        return bTime.localeCompare(aTime);
      });
    case "client-name":
      return sorted.sort((a, b) => (a.clientName ?? "").localeCompare(b.clientName ?? ""));
    case "last-updated-newest":
      return sorted.sort((a, b) => {
        const aTs = a.highlight?.timestamp ?? a.assignedAt ?? "";
        const bTs = b.highlight?.timestamp ?? b.assignedAt ?? "";
        return bTs.localeCompare(aTs);
      });
    case "last-updated-oldest":
      return sorted.sort((a, b) => {
        const aTs = a.highlight?.timestamp ?? a.assignedAt ?? "";
        const bTs = b.highlight?.timestamp ?? b.assignedAt ?? "";
        return aTs.localeCompare(bTs);
      });
    case "dod-oldest":
      return sorted.sort((a, b) => {
        const aD = a.dateOfDeath ?? "";
        const bD = b.dateOfDeath ?? "";
        return aD.localeCompare(bD);
      });
    default:
      return sorted;
  }
}

interface NextStepCellProps {
  highlight: LawyerEstateRow["highlight"];
  onAction?: (e: React.MouseEvent) => void;
  isLoading?: boolean;
}

function NextStepCell({ highlight, onAction, isLoading }: NextStepCellProps) {
  if (!highlight) return null;

  const { category, nextStep } = highlight;

  if (category === "action") {
    return (
      <button
        onClick={onAction}
        disabled={isLoading}
        className="flex h-10 items-center gap-2 rounded-md border border-alert bg-white px-4 py-1.5 text-sm font-medium text-primary hover:bg-subtle transition-colors disabled:opacity-50"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-warning" />
        )}
        {nextStep}
        {!isLoading && <ArrowRight className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <button
      onClick={onAction}
      disabled={isLoading}
      className="flex items-center gap-2.5 text-sm font-medium text-primary hover:text-secondary transition-colors disabled:opacity-50"
    >
      {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
      {nextStep}
      {!isLoading && <ArrowRight className="h-4 w-4" />}
    </button>
  );
}

interface TableRowProps {
  row: LawyerEstateRow;
  onClick: () => void;
  onAction: () => void;
  isActionLoading?: boolean;
}

function EstateTableRow({ row, onClick, onAction, isActionLoading }: TableRowProps) {
  const locationData = parseLocation(row.location);
  const hasInventory = row.assetCount > 0 || row.liabilityCount > 0;
  const clientSubtitle = row.clientRole;

  return (
    <tr
      onClick={onClick}
      className="h-[80px] border-b border-primary last:border-b-0 cursor-pointer transition-colors hover:bg-subtle"
    >
      <td className="p-4 align-middle">
        {row.clientName ? (
          <>
            <span className="block truncate text-sm font-medium leading-relaxed text-primary">
              {row.clientName}
            </span>
            <span className={cn("text-2xs", clientSubtitle ? "text-secondary" : "text-tertiary")}>
              {clientSubtitle ?? "—"}
            </span>
          </>
        ) : row.clientEmail ? (
          <span className="block truncate text-sm text-secondary">{row.clientEmail}</span>
        ) : (
          <span className="text-2xs text-tertiary">—</span>
        )}
      </td>

      <td className="py-2 pr-3 align-middle">
        {row.decedentName ? (
          <>
            <span className="block truncate text-sm font-medium leading-relaxed text-primary">
              {row.decedentName}
            </span>
            <span className={cn("text-2xs", row.dateOfDeath ? "text-secondary" : "text-tertiary")}>
              {row.dateOfDeath
                ? `DOD: ${formatPersonDateForDisplay(row.dateOfDeath, { month: "2-digit", day: "2-digit", year: "numeric" })}`
                : "—"}
            </span>
          </>
        ) : (
          <span className="text-2xs text-tertiary">—</span>
        )}
      </td>

      <td className="py-2 pr-3 align-middle">
        {row.proceedingType ? (
          <>
            <span className="block truncate text-sm font-medium leading-relaxed text-primary">
              {row.proceedingType}
            </span>
            {row.authoritySuffix && (
              <span className="text-2xs text-secondary">{row.authoritySuffix}</span>
            )}
          </>
        ) : (
          <span className="text-2xs text-tertiary">—</span>
        )}
      </td>

      <td className="py-4 pr-4 align-middle">
        {row.highlight ? (
          <>
            <span
              className={cn(
                "block text-sm font-medium",
                row.highlight.category === "action" ? "text-warning" : "text-primary",
              )}
            >
              {row.highlight.label}
            </span>
            {row.highlight.timeAgo && (
              <span className="text-2xs text-secondary">{row.highlight.timeAgo}</span>
            )}
          </>
        ) : (
          <span className="text-2xs text-tertiary">—</span>
        )}
      </td>

      <td className="align-middle">
        <NextStepCell
          highlight={row.highlight}
          isLoading={isActionLoading}
          onAction={(e) => {
            e.stopPropagation();
            onAction();
          }}
        />
      </td>

      <td className="py-2 pr-3 align-middle">
        {hasInventory ? (
          <>
            <span className="block text-sm font-medium text-primary">
              {formatCompactDollars(row.inventoryTotal)}
            </span>
            <span className="text-2xs text-secondary">
              {row.assetCount} {row.assetCount === 1 ? "asset" : "assets"}, {row.liabilityCount}{" "}
              {row.liabilityCount === 1 ? "liability" : "liabilities"}
            </span>
          </>
        ) : (
          <span className="text-2xs text-tertiary">—</span>
        )}
      </td>

      <td className="p-4 align-middle">
        {locationData ? (
          <>
            <span className="block text-sm font-medium leading-relaxed text-primary">
              {locationData.label}
            </span>
            <span className="text-2xs text-secondary">{locationData.detail}</span>
          </>
        ) : (
          <span className="text-2xs text-tertiary">—</span>
        )}
      </td>
    </tr>
  );
}

export const LawyerDashboard = () => {
  const { data: lawyerPersons, isLoading, error } = useLawyerPersons();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, authUser } = useAppContext();
  const { isDemoMode } = useDemoMode();
  const navigateToDemoEstate = useNavigateToDemoEstate();
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isOnboardingCalendlyOpen, setIsOnboardingCalendlyOpen] = useState(false);
  const { resendClientInvitation } = useLawyerClientInvitations();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("action");
  const [filters, setFilters] = useState<DashboardFilters>({ ...EMPTY_FILTERS });

  const activeFilterCount =
    filters.proceeding.length +
    filters.authority.length +
    (filters.inventorySize ? 1 : 0) +
    filters.nextStep.length +
    (filters.state ? 1 : 0) +
    (filters.lastUpdated ? 1 : 0);
  // TODO: re-enable welcome modal once UX is fixed (ELA-1510)
  // const { isDismissed, isFetched: welcomeFetched, dismiss } = useLawyerWelcomeSeen();

  const customizations = (user?.customizations as LawyerCustomizations | null) ?? {};
  const viewedClients = customizations.lawyer_viewed_clients ?? [];

  const userQueryKey = ["app-user-data", authUser?.id] as const;

  const markViewedMutation = useMutation({
    mutationFn: async (estateId: number) => {
      if (!user?.user_id) return;
      if (viewedClients.includes(estateId)) return;
      const nextCustomizations: LawyerCustomizations = {
        ...customizations,
        lawyer_viewed_clients: [...viewedClients, estateId],
      };
      const { error: updateError } = await supabase
        .from("users")
        .update({ customizations: nextCustomizations })
        .eq("user_id", user.user_id);
      if (updateError) throw updateError;
    },
    onMutate: async (estateId: number) => {
      if (!user || viewedClients.includes(estateId)) return;
      await queryClient.cancelQueries({ queryKey: userQueryKey });
      const previous = queryClient.getQueryData(userQueryKey);
      queryClient.setQueryData(
        userQueryKey,
        (data: { user: typeof user; person: unknown } | undefined) => {
          if (!data?.user) return data;
          const prevCustomizations =
            (data.user.customizations as LawyerCustomizations | null) ?? {};
          const prevViewed = prevCustomizations.lawyer_viewed_clients ?? [];
          return {
            ...data,
            user: {
              ...data.user,
              customizations: {
                ...prevCustomizations,
                lawyer_viewed_clients: [...prevViewed, estateId],
              },
            },
          };
        },
      );
      return { previous };
    },
    onError: (_err, _estateId, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(userQueryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKey });
    },
  });

  const handleViewClient = (estateId: number, tab?: string) => {
    markViewedMutation.mutate(estateId);
    const opts = tab ? { state: { tab } } : undefined;
    if (isDemoMode) {
      navigateToDemoEstate(estateId, opts);
    } else {
      navigate(LAWYER_ROUTES.CLIENT_DETAIL.replace(":userId", String(estateId)), opts);
    }
  };

  const subtitle = useMemo(() => {
    if (!lawyerPersons || lawyerPersons.length === 0) return undefined;
    const total = lawyerPersons.length;
    const totalInventory = lawyerPersons.reduce((sum, row) => sum + row.inventoryTotal, 0);
    const actionCount = lawyerPersons.filter((row) => row.highlight?.category === "action").length;
    return `${total} open ${total === 1 ? "matter" : "matters"} · ${formatCompactDollars(totalInventory)} in administration · ${actionCount} need action`;
  }, [lawyerPersons]);

  const filteredAndSorted = useMemo(() => {
    if (!lawyerPersons) return [];
    let rows = lawyerPersons;

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      rows = rows.filter(
        (row) =>
          row.clientName?.toLowerCase().includes(term) ||
          row.clientEmail?.toLowerCase().includes(term) ||
          row.decedentName?.toLowerCase().includes(term) ||
          row.location?.toLowerCase().includes(term),
      );
    }

    if (filters.proceeding.length > 0) {
      rows = rows.filter((r) => filters.proceeding.includes(r.proceedingType));
    }
    if (filters.authority.length > 0) {
      rows = rows.filter((r) => filters.authority.includes(r.authoritySuffix));
    }
    if (filters.inventorySize) {
      const thresholds: Record<string, number> = {
        "$500K+": 500_000,
        "$1M+": 1_000_000,
        "$5M+": 5_000_000,
      };
      const min = thresholds[filters.inventorySize];
      if (min) rows = rows.filter((r) => r.inventoryTotal >= min);
    }
    if (filters.nextStep.length > 0) {
      rows = rows.filter((r) => {
        if (!r.highlight) return false;
        if (filters.nextStep.includes("Action needed") && r.highlight.category === "action")
          return true;
        if (filters.nextStep.includes("Elayne updates") && r.highlight.category === "passive")
          return true;
        return false;
      });
    }
    if (filters.state) {
      rows = rows.filter((r) => {
        if (!r.location) return false;
        const parsed = parseAddress(r.location);
        const abbr = toStateAbbreviation(parsed.state);
        if (abbr === filters.state) return true;
        const locationUpper = r.location.toUpperCase();
        const stateAbbr = filters.state!;
        const stateName = US_STATES.find((s) => s.value === stateAbbr)?.label.toUpperCase() ?? "";
        const abbrPattern = new RegExp(`\\b${stateAbbr}\\b`);
        const namePattern = stateName ? new RegExp(`\\b${stateName}\\b`) : null;
        return (
          abbrPattern.test(locationUpper) ||
          (namePattern !== null && namePattern.test(locationUpper))
        );
      });
    }
    if (filters.lastUpdated) {
      const now = Date.now();
      const dayMs = 86_400_000;
      rows = rows.filter((r) => {
        const ts = r.highlight?.timestamp ?? r.assignedAt;
        if (!ts) return false;
        const age = now - new Date(ts).getTime();
        if (filters.lastUpdated === "Today") return age < dayMs;
        if (filters.lastUpdated === "Last two weeks") return age < 14 * dayMs;
        if (filters.lastUpdated === "Over two weeks ago") return age >= 14 * dayMs;
        return true;
      });
    }

    return sortRows(rows, sortKey);
  }, [lawyerPersons, searchTerm, sortKey, filters]);

  const activeSortLabel = SORT_OPTIONS.find((o) => o.key === sortKey)?.label ?? "Action needed";

  const titleAction = (
    <Button variant="brand" onClick={() => setIsInviteOpen(true)}>
      <Plus className="h-4 w-4" />
      Invite client
    </Button>
  );

  if (isLoading) {
    return (
      <LawyerLayout title="Estate Matters" titleAction={titleAction}>
        <div className="flex items-center justify-center py-12">
          <CircularProgress />
        </div>
      </LawyerLayout>
    );
  }

  if (error) {
    return (
      <LawyerLayout title="Estate Matters" titleAction={titleAction}>
        <div className="py-12 text-center">
          <p className="text-destructive">Error loading estates. Please try again.</p>
        </div>
      </LawyerLayout>
    );
  }

  const hasEstates = lawyerPersons && lawyerPersons.length > 0;

  return (
    <LawyerLayout title="Estate Matters" subtitle={subtitle} titleAction={titleAction}>
      {!hasEstates ? (
        <DashboardEmptyState
          onInviteClient={() => setIsInviteOpen(true)}
          onViewDemo={() => navigate(DEMO_ROUTES.LAWYER)}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {isDemoMode && (
            <div className="flex items-start gap-3 rounded-lg border border-blue-500 bg-blue-50 p-4">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-sm p-2 border border-primary bg-white">
                <Compass className="size-4 text-primary" aria-hidden="true" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="font-medium text-primary leading-5">You're viewing sample matters</p>
                <p className="text-secondary">
                  The matters below use fictitious data to show how Elayne builds the estate record:
                  documents collected from the family, assets and liabilities organized by
                  disposition path, and open issues flagged for your review. Start a real matter
                  anytime, or schedule an onboarding call
                </p>
                <Button
                  variant="outline"
                  className="mt-2 self-start"
                  onClick={() => setIsOnboardingCalendlyOpen(true)}
                >
                  Schedule onboarding call
                </Button>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                variant="search"
                placeholder="Search by client, decedent, email, or address..."
                className="w-full sm:w-96"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="flex gap-2">
                <FilterPanel
                  filters={filters}
                  onApply={setFilters}
                  onReset={() => setFilters({ ...EMPTY_FILTERS })}
                >
                  <Button variant="outline" className="gap-2">
                    <ListFilter className="h-4 w-4" />
                    Filter by
                    {activeFilterCount > 0 && (
                      <span className="flex size-5 items-center justify-center rounded-full bg-brand text-[10px] font-medium text-white">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </FilterPanel>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <Share className="h-4 w-4" />
                      Export
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[200px] p-4">
                    <div className="mb-3 flex items-center gap-2 text-2xs">
                      <span className="text-secondary">Current view</span>
                      <span className="size-1 rounded-full bg-tertiary" />
                      <span className="text-tertiary">{filteredAndSorted.length} matters</span>
                    </div>
                    <DropdownMenuItem
                      onClick={() => generateDashboardPdf(filteredAndSorted)}
                      className="gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Download PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 text-sm font-medium text-primary">
                  Sort by: {activeSortLabel}
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {SORT_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.key}
                    onClick={() => setSortKey(opt.key)}
                    className={cn(sortKey === opt.key && "font-medium")}
                  >
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="w-full overflow-x-auto rounded-lg border border-primary bg-white">
            <table className="w-full" style={{ tableLayout: "fixed", minWidth: 1360 }}>
              <colgroup>
                <col style={{ width: 200 }} />
                <col style={{ width: 200 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 200 }} />
                <col style={{ width: 200 }} />
                <col style={{ width: 200 }} />
              </colgroup>
              <thead>
                <tr className="border-b border-primary">
                  <th className="h-12 px-4 text-left align-middle text-sm font-normal text-secondary">
                    Client
                  </th>
                  <th className="h-12 pr-3 text-left align-middle text-sm font-normal text-secondary">
                    Decedent
                  </th>
                  <th className="h-12 pr-3 text-left align-middle text-sm font-normal text-secondary">
                    Proceeding
                  </th>
                  <th className="h-12 pr-4 text-left align-middle text-sm font-normal text-secondary">
                    Highlight
                  </th>
                  <th className="h-12 pr-4 text-left align-middle text-sm font-normal text-secondary">
                    Next step
                  </th>
                  <th className="h-12 pr-3 text-left align-middle text-sm font-normal text-secondary">
                    Inventory
                  </th>
                  <th className="h-12 px-4 text-left align-middle text-sm font-normal text-secondary">
                    Location(s)
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSorted.map((row) => (
                  <EstateTableRow
                    key={row.id}
                    row={row}
                    isActionLoading={
                      row.pendingInvitationId
                        ? resendClientInvitation.isPending &&
                          resendClientInvitation.variables?.invitationId === row.pendingInvitationId
                        : false
                    }
                    onClick={() => {
                      if (row.estateId === 0) return;
                      handleViewClient(row.estateId);
                    }}
                    onAction={() => {
                      if (row.pendingInvitationId) {
                        resendClientInvitation.mutate({
                          invitationId: row.pendingInvitationId,
                        });
                        return;
                      }
                      const nextStep = row.highlight?.nextStep;
                      const tab =
                        nextStep === "See progress"
                          ? "activity-log"
                          : nextStep === "Review document"
                            ? "documents"
                            : nextStep === "Review asset" || nextStep === "Review debt"
                              ? "estate-finances"
                              : row.highlight?.action === "nudge_estate_account"
                                ? "estate-account"
                                : row.highlight?.action === "nudge_client" ||
                                    row.highlight?.action === "resend_invite"
                                  ? "people"
                                  : undefined;
                      handleViewClient(row.estateId, tab);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <InviteClientWizard isOpen={isInviteOpen} onClose={() => setIsInviteOpen(false)} />
      {/* <AttorneyWelcomeModal open={showWelcomeModal} onDismiss={dismiss} /> */}
      <CalendlyModal
        isOpen={isOnboardingCalendlyOpen}
        onClose={() => setIsOnboardingCalendlyOpen(false)}
        url={CALENDLY_URLS.ATTORNEY_ONBOARDING}
      />
    </LawyerLayout>
  );
};
